import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { IconLabel } from "@/core/components/IconLabel";
import { Button } from "@/core/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/core/components/ui/card";

interface Props {
  children: ReactNode;
  /** Injectable for tests; jsdom's `location.reload` can't be spied on. */
  reload?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// Sits outside RouterProvider (App.tsx), so no Link or router hooks here.
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("ErrorBoundary caught error", error, info);
  }

  private readonly handleReload = (): void => {
    if (this.props.reload) this.props.reload();
    else window.location.reload();
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      const message = this.state.error?.message ?? "Unknown error";
      return (
        <main className="grid min-h-dvh place-items-center bg-background p-4 text-foreground">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle role="heading" aria-level={1}>
                <IconLabel icon="warning" size="section">
                  Something went wrong
                </IconLabel>
              </CardTitle>
              <CardDescription className="break-words">{message}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={this.handleReload}>Reload</Button>
            </CardContent>
          </Card>
        </main>
      );
    }
    return this.props.children;
  }
}
